import useResponsiveness from '@/mixins/useResponsiveness';
import {
  COUNTABLE_NOTIFICATION_TYPES, NotificationCountType, Notifications, NotificationType
} from '@/models/Notification';
import { clearNotifications, getAllNotifications } from '@/services/NotificationsService';
import useFullPageState from '@/state/fullPageState';
import { computed, ref, watch } from 'vue';
import { createGlobalState, debouncedRef, useDocumentVisibility } from '@vueuse/core';
import useIntervalRefresher, { INTERVAL_RESET, intervalRefresherEventBus } from '@/mixins/useIntervalRefresher';
import {
  REFRESH_INTERVAL_NOTIFICATIONS_BEGINNING,
  REFRESH_INTERVAL_NOTIFICATIONS_ENDING,
  REFRESH_INTERVAL_NOTIFICATIONS_TIMESPAN
} from '@/models/IntervalTimes';
import useMessengerState from '@/state/messenger/messengerState';
import { MESSAGE_EVENT_REFRESH } from '@/models/Messenger';
import { logDebug, logVerbose } from '@/utils/logger';
import useContractOverview from '@/views/my-projects/composables/useContractOverview';
import useContract from '@/views/my-projects/composables/useContract';
import useInvoice from '@/views/my-projects/composables/useInvoice';

const useNotificationState = createGlobalState(() => {
  const { isSmartphoneWidth } = useResponsiveness();
  const { isOnFullPage } = useFullPageState();
  const { eventBus: messengerEventBus } = useMessengerState();
  const { fetchContracts } = useContractOverview();

  const notifications = ref<Notifications | null>(null);
  const showPanel = ref(false);
  const whichPanel = ref<NotificationType | ''>(NotificationType.Messages);
  const panelPos = ref(0);

  const notificationCounts = computed(() => {
    if (!notifications.value) return null;
    return Object.fromEntries(
      Object.entries(notifications.value)
        .filter(([type]) => COUNTABLE_NOTIFICATION_TYPES.includes(type as NotificationType))
        .map(([type, values]) => [type, Object.values(values).reduce((prev, cur) => prev + cur)])
    );
  });

  const totalCount = computed(() => (notificationCounts.value ? Object.values(notificationCounts.value).reduce((prev, cur) => prev + cur) : 0));

  const totalMessages = computed(() => notificationCounts.value?.[NotificationType.Messages] ?? 0);

  const which = computed(() => (isSmartphoneWidth.value ? whichPanel.value : ''));

  const forceFlexibleWidth = ref(false);

  // If notifications have already been saved once, checks whether the number of NewContracts and ChangedContracts has been increased, if so, depending on where the user is, the popup is displayed and a request is made
  async function checkForChangesInTheContracts(newNotifications: Notifications) {
    if (!notifications.value) return;
    const notificationsContract = notifications.value?.[NotificationType.Contracts];
    const newNotificationsContract = newNotifications?.[NotificationType.Contracts];
    const areThereNewContracts = notificationsContract?.[NotificationCountType.NewContracts] < newNotificationsContract?.[NotificationCountType.NewContracts];
    const areThereChangedContracts = notificationsContract?.[NotificationCountType.ChangedContracts] < newNotificationsContract?.[NotificationCountType.ChangedContracts];
    if (areThereChangedContracts || areThereNewContracts) {
      if (window.location.pathname === '/my-projects/contracts') {
        await fetchContracts();
        logVerbose('Contracts have been updated in the background');
      } else if (window.location.pathname.startsWith('/my-projects/contract/')) {
        const { detectChanges } = useContract(); // The call must be in place, because otherwise the userId in useContract is undefined.
        logVerbose('Changes were made to one of the contracts');
        await detectChanges();
      }
    }
  }

  /**
   * Checks for new or changed invoices and refreshes the list if needed.
   * Updates invoices in the background when on the invoices page.
   */
  async function checkForChangesInInvoices(newNotifications: Notifications) {
    if (!notifications.value) return;
    const notificationsInvoices = notifications.value?.[NotificationType.Invoices];
    const newNotificationsInvoices = newNotifications?.[NotificationType.Invoices];
    const areThereNewInvoices = notificationsInvoices?.[NotificationCountType.NewInvoices] < newNotificationsInvoices?.[NotificationCountType.NewInvoices];
    const areThereChangedInvoices = notificationsInvoices?.[NotificationCountType.ChangedInvoices] < newNotificationsInvoices?.[NotificationCountType.ChangedInvoices];
    if (areThereNewInvoices || areThereChangedInvoices) {
      if (window.location.pathname === '/my-projects/invoices') {
        const { fetchInvoices } = useInvoice(); // The call must be in place, because otherwise the invoiceId get in trouble with route is undefined
        await fetchInvoices();
        logVerbose('Invoices have been updated in the background');
      }
    }
  }

  const loadNotifications = async () => {
    if (!isOnFullPage.value) return false;

    const newNotifications = await getAllNotifications();
    if (newNotifications) {
      try {
        await checkForChangesInTheContracts(newNotifications);
        await checkForChangesInInvoices(newNotifications);
      } finally {
        notifications.value = newNotifications;
      }
      return true;
    }
    return false;
  };

  const documentVisibility = useDocumentVisibility();
  const debouncedVisibility = debouncedRef(documentVisibility, 1000);

  watch(debouncedVisibility, (v) => {
    if (v === 'visible') {
      logDebug('Refresh Notifications: Tab Activated');
      loadNotifications();
    }
  }, { immediate: true });

  useIntervalRefresher(() => {
    logDebug('Refresh Notifications: Timer');
    loadNotifications();
  }, {
    beginningInterval: REFRESH_INTERVAL_NOTIFICATIONS_BEGINNING,
    endingInterval: REFRESH_INTERVAL_NOTIFICATIONS_ENDING,
    timeSpan: REFRESH_INTERVAL_NOTIFICATIONS_TIMESPAN,
    name: 'Notifications',
  });

  logDebug('Refresh Notification: Initial');
  loadNotifications().then();

  const resetInterval = (reason = '') => {
    intervalRefresherEventBus.emit(INTERVAL_RESET, reason);
  };

  watch(showPanel, (is) => {
    if (is) resetInterval('Show notification panel');
  });

  watch(totalMessages, (total) => {
    logDebug('totalMessages changed:', total, 'doing a meta refresh');
    messengerEventBus.emit(MESSAGE_EVENT_REFRESH);
  });

  const clear = async (w: NotificationType) => {
    await clearNotifications(w);
    resetInterval('Entries deleted');
    showPanel.value = false;
  };

  return ({
    notifications,
    loadNotifications,
    clear,
    showPanel,
    whichPanel,
    which,
    panelPos,
    resetInterval,
    forceFlexibleWidth,
    notificationCounts,
    totalCount,
  });
});

export default useNotificationState;
